import {
  InstrumentForgeProfile, InstrumentForgeBlendConfig, BlendedProductionOutput,
  ProductionTraits, RhythmSectionSpec, GuitarSpec, KeysAtmosphereSpec,
  SpecialtySpec, MixTarget, PhysicsLayers, DrumSpec, BassSpec, GuitarLayerSpec
} from '../types/instrumentForge.types';
import { extractTraitsFromProfile, blendTraits } from '../data/productionTraits';
import { normalizeInstrumentProfile } from './normalizeProfile';

// ═══════════════════════════════════════════════════════════════
// STRING BLENDING HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Merges two description strings based on dominance
 */
function blendStrings(a: string, b: string, ratio: number, separator: string = "; "): string {
  a = a ?? '';
  b = b ?? '';
  if (ratio >= 90) return a;
  if (ratio <= 10) return b;
  
  // Clean tags if they exist to prevent nested brackets
  const cleanA = a.replace(/[\[\]]/g, '').trim();
  const cleanB = b.replace(/[\[\]]/g, '').trim();
  
  if (ratio > 60) return `${cleanA} (dominant) with ${cleanB} elements`;
  if (ratio < 40) return `${cleanB} (dominant) with ${cleanA} elements`;
  return `Hybrid: ${cleanA} meets ${cleanB}`;
}

/**
 * Blends technical bracketed tags [Key: Value]
 */
function blendPhysicsTags(a: string, b: string, ratio: number, label: string): string {
  a = a ?? '';
  b = b ?? '';
  if (ratio >= 90) return a;
  if (ratio <= 10) return b;

  // Extract the content inside brackets
  const contentA = a.replace(new RegExp(`^\\[${label}:\\s*|\\s*\\]$`, 'gi'), '');
  const contentB = b.replace(new RegExp(`^\\[${label}:\\s*|\\s*\\]$`, 'gi'), '');

  // Split tags
  const tagsA = contentA.split(';').map(t => t.trim()).filter(Boolean);
  const tagsB = contentB.split(';').map(t => t.trim()).filter(Boolean);

  // Take a portion from each based on ratio
  const countA = Math.ceil(tagsA.length * (ratio / 100));
  const countB = Math.ceil(tagsB.length * ((100 - ratio) / 100));

  const merged = [...new Set([...tagsA.slice(0, countA), ...tagsB.slice(0, countB)])];
  return `[${label}: ${merged.join('; ')}]`;
}

/** "Identity: era movement. Tone: tone." -- parts a profile does not have are left out instead of printing "undefined". */
function identityLine(id: { era: string; movement: string; tone: string }): string {
  const who = `${id.era} ${id.movement}`.trim();
  const parts: string[] = [];
  if (who) parts.push(`Identity: ${who}.`);
  if (id.tone) parts.push(`Tone: ${id.tone}.`);
  return parts.join(' ');
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT BLENDERS
// ═══════════════════════════════════════════════════════════════

function blendDrumSpec(a: DrumSpec, b: DrumSpec, ratio: number): DrumSpec {
  return {
    kit: blendStrings(a.kit, b.kit, ratio),
    room: blendStrings(a.room, b.room, ratio),
    character: blendStrings(a.character, b.character, ratio),
    compression: blendStrings(a.compression, b.compression, ratio),
    chain: blendStrings(a.chain || '', b.chain || '', ratio)
  };
}

function blendBassSpec(a: BassSpec, b: BassSpec, ratio: number): BassSpec {
  return {
    instrument: blendStrings(a.instrument, b.instrument, ratio),
    split: blendStrings(a.split || '', b.split || '', ratio),
    lock: blendStrings(a.lock, b.lock, ratio),
    character: blendStrings(a.character, b.character, ratio),
    chain: blendStrings(a.chain || '', b.chain || '', ratio)
  };
}

function blendGuitarLayer(a: GuitarLayerSpec, b: GuitarLayerSpec, ratio: number): GuitarLayerSpec {
  return {
    character: blendStrings(a.character, b.character, ratio),
    chain: blendStrings(a.chain, b.chain, ratio),
    role: blendStrings(a.role, b.role, ratio),
    frequencyFocus: blendStrings(a.frequencyFocus || '', b.frequencyFocus || '', ratio)
  };
}

// ═══════════════════════════════════════════════════════════════
// MAIN ENGINE
// ═══════════════════════════════════════════════════════════════

export function generateBlendedOutput(
  config: InstrumentForgeBlendConfig,
  profiles?: Record<string, InstrumentForgeProfile>
): BlendedProductionOutput | null {
  if (!config.profileAKey) return null;
  const packs = profiles || {};
  const rawA = packs[config.profileAKey];
  if (!rawA) return null;
  // AUTO-102: generator-made profiles are stored in a simpler shape; bring them to the full one first.
  const profileA = normalizeInstrumentProfile(rawA);
  const traitsA = extractTraitsFromProfile(profileA);
  
  // Single Profile Mode
  if (!config.profileBKey || config.blendRatio === 100) {
    const output: BlendedProductionOutput = {
      traits: traitsA,
      name: profileA.name,
      description: profileA.description,
      stylesBlock: identityLine(profileA.identity),
      excludeStyles: profileA.excludeStyles,
      rhythmSection: profileA.rhythmSection,
      guitars: profileA.guitars,
      keysAtmosphere: profileA.keysAtmosphere,
      specialty: profileA.specialty,
      mixTargets: profileA.mixTargets,
      physicsLayers: profileA.physicsLayers,
      fullPromptBlock: '', // Calculated below
      compactPrompt: `${`${profileA.identity.era} ${profileA.identity.movement}`.trim() || profileA.name.replace(/^InstrumentForge:\s*/i, '').trim() || 'Custom'} production.`,
      sunoTags: [...new Set(profileA.genres)].slice(0, 8),
      sourceProfiles: { profileAKey: config.profileAKey, blendRatio: 100 }
    };
    output.fullPromptBlock = assembleFullPrompt(output);
    return output;
  }
  
  // Blend Mode
  const rawB = packs[config.profileBKey];
  if (!rawB) return null;
  const profileB = normalizeInstrumentProfile(rawB);
  
  const traitsB = extractTraitsFromProfile(profileB);
  const ratio = config.blendRatio;
  const blendedTraits = blendTraits(traitsA, traitsB, ratio);
  
  const output: BlendedProductionOutput = {
    traits: blendedTraits,
    name: `${ratio}% ${profileA.name} / ${100 - ratio}% ${profileB.name}`,
    description: `Architectural hybrid: ${profileA.name} foundation with ${profileB.name} modifiers.`,
    stylesBlock: blendStrings(
      `${profileA.identity.era} ${profileA.identity.movement}`.trim(),
      `${profileB.identity.era} ${profileB.identity.movement}`.trim(),
      ratio,
      ' x '
    ),
    // Added comment: Fixed type mismatch by explicitly typing Set and ensuring trimmed string output.
    excludeStyles: Array.from(new Set<string>([...profileA.excludeStyles.split(','), ...profileB.excludeStyles.split(',')].map(s => s.trim()))).join(', '),
    rhythmSection: {
      drums: blendDrumSpec(profileA.rhythmSection.drums, profileB.rhythmSection.drums, ratio),
      bass: blendBassSpec(profileA.rhythmSection.bass, profileB.rhythmSection.bass, ratio)
    },
    guitars: {
      rhythmLeft: blendGuitarLayer(profileA.guitars.rhythmLeft, profileB.guitars.rhythmLeft, ratio),
      rhythmRight: blendGuitarLayer(profileA.guitars.rhythmRight, profileB.guitars.rhythmRight, ratio),
      lead: (profileA.guitars.lead && profileB.guitars.lead) ? blendGuitarLayer(profileA.guitars.lead, profileB.guitars.lead, ratio) : (profileA.guitars.lead || profileB.guitars.lead),
      texture: (profileA.guitars.texture && profileB.guitars.texture) ? blendGuitarLayer(profileA.guitars.texture, profileB.guitars.texture, ratio) : (profileA.guitars.texture || profileB.guitars.texture)
    },
    keysAtmosphere: profileA.keysAtmosphere || profileB.keysAtmosphere, // Logic: prefer A, fallback B
    mixTargets: ratio >= 50 ? profileA.mixTargets : profileB.mixTargets,
    physicsLayers: {
      layer2: {
        header: '{LAYER-2 RHYTHM SECTION PHYSICS}',
        drumBehavior: blendPhysicsTags(profileA.physicsLayers.layer2.drumBehavior, profileB.physicsLayers.layer2.drumBehavior, ratio, 'Drum_Behavior'),
        drumChain: blendPhysicsTags(profileA.physicsLayers.layer2.drumChain, profileB.physicsLayers.layer2.drumChain, ratio, 'Drum_Chain'),
        bassBehavior: blendPhysicsTags(profileA.physicsLayers.layer2.bassBehavior, profileB.physicsLayers.layer2.bassBehavior, ratio, 'Bass_Behavior'),
        bassChain: blendPhysicsTags(profileA.physicsLayers.layer2.bassChain, profileB.physicsLayers.layer2.bassChain, ratio, 'Bass_Chain'),
      },
      layer3: {
        header: '{LAYER-3 GUITAR PHYSICS}',
        guitarBehavior: blendPhysicsTags(profileA.physicsLayers.layer3.guitarBehavior, profileB.physicsLayers.layer3.guitarBehavior, ratio, 'Guitar_Behavior'),
        rhythmLChain: blendPhysicsTags(profileA.physicsLayers.layer3.rhythmLChain, profileB.physicsLayers.layer3.rhythmLChain, ratio, 'Rhythm_L_Chain'),
        rhythmRChain: blendPhysicsTags(profileA.physicsLayers.layer3.rhythmRChain, profileB.physicsLayers.layer3.rhythmRChain, ratio, 'Rhythm_R_Chain'),
        leadChain: (profileA.physicsLayers.layer3.leadChain && profileB.physicsLayers.layer3.leadChain) 
          ? blendPhysicsTags(profileA.physicsLayers.layer3.leadChain, profileB.physicsLayers.layer3.leadChain, ratio, 'Lead_Chain')
          : (profileA.physicsLayers.layer3.leadChain || profileB.physicsLayers.layer3.leadChain),
      }
    },
    fullPromptBlock: '', // Calculated below
    compactPrompt: `Blended Architecture: ${ratio}% ${profileA.identity.movement}.`,
    sunoTags: [...new Set([...profileA.genres, ...profileB.genres])].slice(0, 10),
    sourceProfiles: { profileAKey: config.profileAKey, profileBKey: config.profileBKey, blendRatio: ratio }
  };

  output.fullPromptBlock = assembleFullPrompt(output);
  return output;
}

/**
 * Assembles the final prose for the Suno prompt
 */
function assembleFullPrompt(out: BlendedProductionOutput): string {
  const lines: string[] = [];
  lines.push(`[INSTRUMENTFORGE PRODUCTION ARCHITECTURE - ${out.name.toUpperCase()}]`);
  lines.push(`SUMMARY: ${out.description}`);
  lines.push(`STYLE: ${out.stylesBlock}`);
  lines.push('');
  lines.push(`[Drums: ${out.rhythmSection.drums.kit}. ${out.rhythmSection.drums.character}]`);
  lines.push(`[Bass: ${out.rhythmSection.bass.instrument}. ${out.rhythmSection.bass.character}]`);
  lines.push(`[Guitars: ${out.guitars.rhythmLeft.character}]`);
  lines.push('');
  lines.push(out.physicsLayers.layer2.header);
  lines.push(out.physicsLayers.layer2.drumBehavior);
  lines.push(out.physicsLayers.layer2.drumChain);
  lines.push(out.physicsLayers.layer2.bassBehavior);
  lines.push(out.physicsLayers.layer2.bassChain);
  lines.push('');
  lines.push(out.physicsLayers.layer3.header);
  lines.push(out.physicsLayers.layer3.guitarBehavior);
  lines.push(out.physicsLayers.layer3.rhythmLChain);
  lines.push(out.physicsLayers.layer3.rhythmRChain);
  
  return lines.join('\n');
}
